import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePermissions } from '@/hooks/usePermissions'
import { PermissionGate } from '@/components/PermissionGate'
import { 
  Home, 
  FileText, 
  Calendar, 
  ChefHat, 
  MapPin, 
  Users, 
  Settings,
  LogOut,
  BarChart3,
  Inbox,
  HelpCircle,
  Sliders
} from 'lucide-react'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
  onClose?: () => void
}

const menuItems = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: Home,
    permission: 'dashboard_view'
  },
  {
    label: 'Vistorias',
    path: '/vistorias',
    icon: FileText,
    permission: 'vistoria_view'
  },
  {
    label: 'Calendários',
    path: '/calendarios',
    icon: Calendar,
    permission: 'calendario_view'
  },
  {
    label: 'Cardápios',
    path: '/cardapios',
    icon: ChefHat,
    permission: 'cardapio_view'
  },
  {
    label: 'Geolocalização',
    path: '/geolocations',
    icon: MapPin,
    permission: 'geolocation_view'
  },
  {
    label: 'Usuários',
    path: '/usuarios',
    icon: Users,
    permission: 'user_view'
  },
  {
    label: 'Parâmetros',
    path: '/parametros',
    icon: Sliders,
    permission: 'parametro_view'
  }
]

const bottomMenuItems = [
  {
    label: 'Relatórios',
    path: '/relatorios',
    icon: BarChart3,
    permission: 'relatorio_view'
  },
  {
    label: 'Configurações',
    path: '/configuracoes',
    icon: Settings,
    permission: 'config_view'
  },
  {
    label: 'Ajuda',
    path: '/ajuda',
    icon: HelpCircle,
    permission: 'help_view'
  }
]

export function Sidebar({ isOpen, onToggle, onClose }: SidebarProps) {
  const location = useLocation()
  const { logout } = useAuth()

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const handleLogout = () => {
    logout()
  }

  const handleLinkClick = () => {
    // Fechar sidebar em mobile e tablet pequeno
    if (window.innerWidth < 768) {
      onClose ? onClose() : onToggle()
    }
  }

  // Fechar sidebar quando redimensionar para desktop
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isOpen) {
        onClose ? onClose() : onToggle()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen, onClose, onToggle])

  return (
    <>
      {/* Overlay para mobile e tablet */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden transition-opacity duration-300"
          onClick={onToggle}
          style={{ top: '4rem' }}
        />
      )}

      {/* Sidebar responsivo */}
      <aside 
        className={`
          fixed top-16 left-0 transition-all duration-300 ease-in-out
          bg-white border-r border-gray-200 shadow-lg
          h-[calc(100vh-4rem)] z-30
          
          /* Controle de visibilidade */
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          
          /* Desktop: sempre visível */
          md:translate-x-0
          
          /* Largura responsiva */
          w-56 sm:w-64 lg:w-72
        `}
        aria-label="Sidebar"
      >
        <div className="h-full px-3 py-4 overflow-y-auto overscroll-contain">
          {/* Header do sidebar apenas em mobile */}
          <div className="mb-4 md:hidden">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
              <button
                onClick={onToggle}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Fechar menu"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <hr className="border-gray-200" />
          </div>

          {/* Menu principal */}
          <ul className="space-y-1 md:space-y-2 font-medium">
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <PermissionGate key={item.path} permission={item.permission}>
                  <li>
                    <Link
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`
                        flex items-center rounded-lg group transition-all duration-200
                        /* Mobile e tablet: padding menor */
                        p-2 text-sm
                        /* Desktop: padding maior */
                        md:p-3 md:text-base
                        ${isActive(item.path)
                          ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-500 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                    >
                      <Icon className={`
                        transition duration-200 flex-shrink-0
                        /* Mobile: ícone menor */
                        w-4 h-4
                        /* Tablet: ícone padrão */
                        sm:w-5 sm:h-5
                        /* Desktop: ícone maior */
                        md:w-6 md:h-6
                        ${isActive(item.path)
                          ? 'text-blue-600'
                          : 'text-gray-500 group-hover:text-gray-700'
                        }
                      `} />
                      <span className={`
                        transition duration-200 font-medium
                        /* Mobile: espaçamento menor */
                        ms-2
                        /* Tablet e desktop: espaçamento maior */
                        sm:ms-3
                        /* Desktop: texto maior */
                        md:text-base
                      `}>
                        {item.label}
                      </span>
                    </Link>
                  </li>
                </PermissionGate>
              )
            })}
          </ul>

          {/* Separador e menu secundário */}
          <ul className="pt-3 mt-3 md:pt-4 md:mt-4 space-y-1 md:space-y-2 font-medium border-t border-gray-200">
            {bottomMenuItems.map((item) => {
              const Icon = item.icon
              return (
                <PermissionGate key={item.path} permission={item.permission}>
                  <li>
                    <Link
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`
                        flex items-center transition-all duration-200 rounded-lg hover:bg-gray-50 group
                        /* Mobile e tablet: padding menor */
                        p-2 text-sm text-gray-700
                        /* Desktop: padding maior */
                        md:p-3 md:text-base
                        ${isActive(item.path) ? 'bg-blue-50 text-blue-700 shadow-sm' : ''}
                      `}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-500 transition duration-200 group-hover:text-gray-700 flex-shrink-0" />
                      <span className="ms-2 sm:ms-3">{item.label}</span>
                    </Link>
                  </li>
                </PermissionGate>
              )
            })}
            
            {/* Botão de logout */}
            <li>
              <button
                onClick={handleLogout}
                className="w-full flex items-center transition-all duration-200 rounded-lg hover:bg-red-50 group p-2 text-sm md:p-3 md:text-base text-red-600"
              >
                <LogOut className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-red-500 transition duration-200 group-hover:text-red-600 flex-shrink-0" />
                <span className="ms-2 sm:ms-3 font-medium">Sair</span>
              </button>
            </li>
          </ul>
        </div>
      </aside>
    </>
  )
}

export default Sidebar
